import { queryClient } from "@api/queryClient";
import { useSessionStore } from "@auth/sessionStore";
import { calendarClient } from "@calendar/calendarClient";
import { calendarKeys, useScheduledVisits } from "@calendar/calendarQueries";
import {
  type CalendarVisitGroup,
  groupCalendarVisits,
  summarizeCalendarGroup,
} from "@calendar/groupCalendarVisits";
import {
  formatDayNumber,
  formatWeekday,
  isCurrentDay,
  parseDateParam,
  rangeDays,
  shiftDays,
  toISODate,
} from "@calendar/weekDates";
import { PatientClientName } from "@components/PatientClientName";
import { cn } from "@scribenote/brian-design-system";
import { formatTime } from "@utils/format-date";
import { useOpenVisit } from "@visits/useOpenVisit";
import {
  isVisitCancelled,
  type StageStatus,
  type TodayVisit,
} from "@visits/visitsClient";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

const MIN_ROW_PX = 64;
const MIN_BLOCK_PX = 24;
// Breathing room above the first / below the last hour label so they aren't
// flush against the grid edge.
const GRID_PAD_PX = 10;
// A block needs room for its header (name + time) plus a line before the
// reason/type description is worth showing.
const DESCRIPTION_MIN_BLOCK_PX = 44;
const DEFAULT_DURATION_HOURS = 0.5;
const WINDOW_MIN_HOUR = 9;
const WINDOW_MAX_HOUR = 17;

const MS_PER_HOUR = 3_600_000;

const decimalHour = (iso: string): number => {
  const d = new Date(iso);
  return d.getHours() + d.getMinutes() / 60;
};

const startOfDayMs = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

interface DaySegment {
  group: CalendarVisitGroup;
  start: number;
  end: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

// The hour slice (0–24) a visit occupies on `day`, or null if it doesn't
// reach it. A visit spanning multiple days is clamped to each day's edges:
// the first day runs start→midnight, whole days in between fill 0→24, and the
// final day runs midnight→end. Without that clamp the block would invert or
// overflow the grid (end hour-of-day < start hour-of-day on the next day).
const daySegment = (
  group: CalendarVisitGroup,
  day: Date,
): DaySegment | null => {
  // Every visit in a group shares this exact slot by construction.
  const visit = group.visits[0];
  const startMs = new Date(visit.scheduledStart as string).getTime();
  const endMs = visit.scheduledEnd
    ? new Date(visit.scheduledEnd).getTime()
    : startMs + DEFAULT_DURATION_HOURS * MS_PER_HOUR;
  const dayStartMs = startOfDayMs(day);
  const dayEndMs = dayStartMs + 24 * MS_PER_HOUR;
  if (startMs >= dayEndMs || endMs <= dayStartMs) return null;
  const start =
    startMs <= dayStartMs ? 0 : decimalHour(visit.scheduledStart as string);
  const end =
    endMs >= dayEndMs
      ? 24
      : visit.scheduledEnd
        ? decimalHour(visit.scheduledEnd)
        : start + DEFAULT_DURATION_HOURS;
  return {
    group,
    start,
    end,
    continuesBefore: startMs < dayStartMs,
    continuesAfter: endMs > dayEndMs,
  };
};

// Stage accents in ink tones. Active is the working ink - it's a working
// state, not a danger (the old red was legacy semantics); done is the warm
// annotation ink (finished, filed away); anything else stays faint.
const stageAccent = (status: StageStatus | undefined): string => {
  switch (status) {
    case "active":
      return "bg-ink-working";
    case "done":
      return "bg-annotation";
    default:
      return "bg-faint";
  }
};

interface PositionedVisit extends DaySegment {
  colIndex: number;
  colCount: number;
}

/**
 * Assign overlapping visits to side-by-side columns. Visits are grouped into
 * clusters of chained overlaps; within a cluster each visit takes the lowest
 * free column, and every cluster member shares the cluster's column count so
 * blocks tile the day width evenly.
 */
const layoutDay = (segments: DaySegment[]): PositionedVisit[] => {
  const items: PositionedVisit[] = segments
    .map((seg) => ({ ...seg, colIndex: 0, colCount: 1 }))
    .sort((a, b) => a.start - b.start || a.end - b.end);

  let cluster: PositionedVisit[] = [];
  let colEnds: number[] = [];
  const flush = () => {
    for (const it of cluster) it.colCount = colEnds.length;
    cluster = [];
    colEnds = [];
  };

  for (const it of items) {
    if (colEnds.length && it.start >= Math.max(...colEnds)) flush();
    const free = colEnds.findIndex((end) => end <= it.start);
    if (free === -1) {
      it.colIndex = colEnds.length;
      colEnds.push(it.end);
    } else {
      it.colIndex = free;
      colEnds[free] = it.end;
    }
    cluster.push(it);
  }
  flush();
  return items;
};

// Overfetch one day before the visible range. The endpoint keys off
// `scheduledStart`, so an appointment that began the previous day and runs into
// view would otherwise be missing; `daySegment` clips the extra day back out.
const fetchRange = (anchor: Date, dayCount: number): [string, string] => {
  const span = rangeDays(anchor, dayCount);
  return [toISODate(shiftDays(span[0], -1)), toISODate(span[span.length - 1])];
};

export const WeekView = ({
  anchor,
  dayCount,
}: {
  anchor: Date;
  dayCount: number;
}) => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const openVisitHook = useOpenVisit();
  const openVisit = (visit: TodayVisit) =>
    openVisitHook({
      visitUuid: visit.id,
      patientUuid: visit.patientId,
      patientName: visit.patientName,
      clientName: visit.clientName,
      scheduledStart: visit.scheduledStart ?? null,
      currentRoomUuid: visit.currentRoomUuid,
    });

  const days = rangeDays(anchor, dayCount);
  const [fetchStart, fetchEnd] = fetchRange(anchor, dayCount);
  const { data: visits } = useScheduledVisits(fetchStart, fetchEnd, sessionKey);

  // Warm the neighbouring ranges so ◀ / ▶ resolve from cache — the change then
  // animates over real content instead of flashing the suspense skeleton.
  const prevAnchorKey = toISODate(shiftDays(anchor, -dayCount));
  const nextAnchorKey = toISODate(shiftDays(anchor, dayCount));
  useEffect(() => {
    for (const anchorKey of [prevAnchorKey, nextAnchorKey]) {
      const [start, end] = fetchRange(parseDateParam(anchorKey), dayCount);
      void queryClient.prefetchQuery({
        queryKey: calendarKeys.range(sessionKey, start, end),
        queryFn: () => calendarClient.range(start, end),
      });
    }
  }, [sessionKey, prevAnchorKey, nextAnchorKey, dayCount]);

  const scheduled = visits.filter((v) => v.scheduledStart);
  const unscheduled = visits.filter((v) => !v.scheduledStart);
  const scheduledGroups = groupCalendarVisits(scheduled);

  // A visit appears on every day its [start, end] interval touches, sliced to
  // that day's edges, so multi-day visits continue across the following days.
  const layoutByDay = days.map((day) =>
    layoutDay(
      scheduledGroups
        .map((group) => daySegment(group, day))
        .filter((s): s is DaySegment => s !== null),
    ),
  );

  let windowStart = WINDOW_MIN_HOUR;
  let windowEnd = WINDOW_MAX_HOUR;
  for (const positioned of layoutByDay) {
    for (const p of positioned) {
      windowStart = Math.min(windowStart, Math.floor(p.start));
      windowEnd = Math.max(windowEnd, Math.ceil(p.end));
    }
  }
  windowStart = Math.max(0, windowStart);
  windowEnd = Math.min(24, Math.max(windowEnd, windowStart + 1));

  const rows = windowEnd - windowStart;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewportHeight, setViewportHeight] = useState(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() =>
      setViewportHeight(el.clientHeight),
    );
    observer.observe(el);
    setViewportHeight(el.clientHeight);
    return () => observer.disconnect();
  }, []);

  const rowPx = Math.max(
    MIN_ROW_PX,
    Math.floor((viewportHeight - 2 * GRID_PAD_PX) / rows),
  );
  const gridHeight = rows * rowPx;
  const contentHeight = gridHeight + 2 * GRID_PAD_PX;
  // Only scroll when the grid genuinely overflows; otherwise clip so a stray
  // sub-pixel can't produce a phantom scrollbar.
  const needsScroll = contentHeight > viewportHeight;

  return (
    <div className="flex h-full min-w-0 flex-col">
      {/* Day heads: mono annotation caps over the date, today's date circled
          in ink. */}
      <div className="flex border-b border-rule">
        <div className="w-14 shrink-0" />
        {days.map((day) => {
          const today = isCurrentDay(day);
          return (
            <div
              key={day.toISOString()}
              className="flex min-w-0 flex-1 flex-col items-center gap-1 px-1 py-2"
            >
              <span
                className={cn(
                  "text-annotation-caps",
                  today ? "text-ink" : "text-annotation",
                )}
              >
                {formatWeekday(day)}
              </span>
              <span
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-full font-display text-base font-medium",
                  today ? "bg-ink text-ink-inverse" : "text-ink",
                )}
              >
                {formatDayNumber(day)}
              </span>
            </div>
          );
        })}
      </div>

      {unscheduled.length > 0 && (
        // Visits without a slot sit in a recessed tray above the ruling, as
        // loose slips of paper.
        <div className="flex border-b border-rule bg-recess/60">
          <div className="flex w-14 shrink-0 items-center justify-center px-1 py-2 text-center text-annotation-caps text-annotation">
            No time
          </div>
          <div className="flex min-w-0 flex-1 flex-wrap gap-2 p-2">
            {unscheduled.map((v) => {
              const cancelled = isVisitCancelled(v);
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => openVisit(v)}
                  className={cn(
                    "relative min-w-0 max-w-full cursor-pointer overflow-hidden rounded-paper border border-rule bg-card py-1 pl-2.5 pr-2 text-left shadow-lift",
                    "transition duration-150 ease-paper hover:-translate-y-px hover:shadow-lift-lg motion-reduce:transition-none",
                    "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
                    cancelled && "opacity-60",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "absolute left-0 top-0 h-full w-1",
                      stageAccent(v.currentStage?.status),
                    )}
                  />
                  <p
                    className={cn(
                      "truncate text-xs font-semibold text-ink",
                      cancelled && "line-through",
                    )}
                  >
                    <PatientClientName
                      patientName={v.patientName}
                      clientName={v.clientName}
                    />
                  </p>
                  <p className="truncate text-[11px] text-annotation">
                    {v.reason ?? v.visitType}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div
        ref={scrollRef}
        className={`relative flex-1 ${needsScroll ? "overflow-y-auto" : "overflow-hidden"}`}
      >
        <div className="flex" style={{ height: contentHeight }}>
          <div className="relative w-14 shrink-0">
            {Array.from({ length: rows + 1 }).map((_, i) => (
              <div
                key={i}
                style={{ top: GRID_PAD_PX + i * rowPx }}
                className="absolute right-2 -translate-y-1/2 font-mono text-[0.625rem] text-annotation"
              >
                {formatHourLabel(windowStart + i)}
              </div>
            ))}
          </div>

          {days.map((day, dayIndex) => {
            const positioned = layoutByDay[dayIndex];
            return (
              <div
                key={day.toISOString()}
                className="relative min-w-0 flex-1 border-l border-rule"
              >
                {isCurrentDay(day) && (
                  // A whisper of recess over today's column, under the ruling.
                  <div className="pointer-events-none absolute inset-0 bg-recess/60" />
                )}
                {/* The hour ruling: the grid is ruled paper. */}
                <div
                  className="pointer-events-none absolute inset-x-0 grid"
                  style={{
                    top: GRID_PAD_PX,
                    height: gridHeight,
                    gridTemplateRows: `repeat(${rows}, 1fr)`,
                  }}
                >
                  {Array.from({ length: rows }).map((_, i) => (
                    <div
                      key={i}
                      className={cn(
                        "border-t border-rule",
                        i === rows - 1 && "border-b",
                      )}
                    />
                  ))}
                </div>
                {positioned.map((p) => (
                  <VisitBlock
                    key={p.group.visits.map(({ id }) => id).join("-")}
                    item={p}
                    windowStart={windowStart}
                    rowPx={rowPx}
                    offsetPx={GRID_PAD_PX}
                    onSelect={openVisit}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const VisitBlock = ({
  item,
  windowStart,
  rowPx,
  offsetPx,
  onSelect,
}: {
  item: PositionedVisit;
  windowStart: number;
  rowPx: number;
  offsetPx: number;
  onSelect: (visit: TodayVisit) => void;
}) => {
  const {
    group,
    start,
    end,
    colIndex,
    colCount,
    continuesBefore,
    continuesAfter,
  } = item;
  // Known limitation (WEB-3219): a multi-patient block opens its first visit;
  // the other patients in the slot aren't individually reachable from the
  // calendar. Collapsing is the intended declutter fix — surfacing each sibling
  // (a picker/expanded view) is a separate design decision, not done here.
  const visit = group.visits[0];
  const { isMultiPatient, cancelled, accentStatus, title, subtitle } =
    summarizeCalendarGroup(group);
  const subtitleText = isMultiPatient
    ? subtitle
    : (visit.reason ?? visit.visitType);
  // Bleed past the grid edge into the padding when the visit crosses the day
  // boundary, and square that corner, so the block visibly runs off rather than
  // reading as a normal start/end.
  const top =
    offsetPx + (start - windowStart) * rowPx - (continuesBefore ? offsetPx : 0);
  const rawHeight = (end - start) * rowPx;
  const height =
    Math.max(rawHeight, MIN_BLOCK_PX) +
    (continuesBefore ? offsetPx : 0) +
    (continuesAfter ? offsetPx : 0);
  const widthPct = 100 / colCount;

  return (
    <button
      type="button"
      onClick={() => onSelect(visit)}
      aria-label={isMultiPatient && title ? `Open ${title}` : undefined}
      style={{
        top,
        height,
        left: `calc(${colIndex * widthPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
      }}
      className={cn(
        "absolute cursor-pointer overflow-hidden border border-rule bg-card text-left shadow-lift",
        "transition duration-150 ease-paper hover:-translate-y-px hover:shadow-lift-lg motion-reduce:transition-none",
        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
        continuesBefore ? "rounded-t-none border-t-0" : "rounded-t-paper",
        continuesAfter ? "rounded-b-none border-b-0" : "rounded-b-paper",
        cancelled && "opacity-60",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute left-0 top-0 h-full w-1",
          stageAccent(accentStatus),
        )}
      />
      <div className="overflow-hidden py-1 pl-2.5 pr-1.5">
        <div className="flex min-w-0 items-start gap-1.5">
          <p
            className={cn(
              "min-w-0 flex-1 truncate text-xs font-semibold text-ink",
              cancelled && "line-through",
            )}
          >
            {title ?? (
              <PatientClientName
                patientName={visit.patientName}
                clientName={visit.clientName}
              />
            )}
          </p>
          {colCount === 1 && (
            <span className="shrink-0 whitespace-nowrap font-mono text-[0.625rem] text-annotation">
              {formatTime(visit.scheduledStart as string)}
              {visit.scheduledEnd && ` – ${formatTime(visit.scheduledEnd)}`}
            </span>
          )}
        </div>
        {height >= DESCRIPTION_MIN_BLOCK_PX && subtitleText && (
          <p className="truncate text-[11px] text-annotation">{subtitleText}</p>
        )}
      </div>
    </button>
  );
};

const formatHourLabel = (hour: number): string => {
  const h = ((hour + 11) % 12) + 1;
  const suffix = hour < 12 || hour === 24 ? "am" : "pm";
  return `${h}${suffix}`;
};
